/* Keeps the cart's gift lines in step with the gift tiers
   (snippets/cart-gift-tiers.liquid).

   The snippet hands over each tier's gift variant and threshold, whether the
   cart qualifies, and the gift lines it already holds. A missing gift is added
   with its _gift property, a gift the cart no longer qualifies for is removed,
   and a gift line is held at its tier's target quantity (1, except under
   Black Friday's per-unit rule).

   Two ways in:

   - window.settleGiftsBeforePaint(), the one a quantity change uses. The
     drawer markup /cart/change.js answers with already carries the server's
     verdict for the cart *after* the change, so the difference can be applied
     and the gift line brought back in the same POST's sections — in the first
     paint, rather than appearing a render later. That second render is what
     made a gift flicker in.
   - <cart-gift-sync>, for every other way the cart moves (add to cart, a
     discount landing). It waits a moment and then reads the live cart, because
     a cart passes through in-between states: add Voyage and it is full price
     until its add-on code lands a beat later, which briefly put a $537 cart
     over the $550 tier, so a gift was added and taken away again a second
     later. If the drawer re-renders in the meantime this element is gone and
     the new one decides.

   A custom element for the same reason as the upsell carousel: the drawer
   swaps its markup in after every cart change, and an element upgrades on
   insertion where a script inside that markup would never run. */
/* One cart write at a time, shared by cart.js and cart-gift-sync.js. Shopify
   does not serialise cart writes: two in flight at once each start from the
   cart as it was, and the last to land wins. A gift add that went out while a
   bundle's add-on code was still being applied came back after it and left the
   cart without the code ($368 became $408). Shared by every file that writes the cart.

   run(fn) queues fn behind every write already queued and resolves with what
   it returns. Take it only at the outermost step (a whole discount pass, a
   whole gift reconcile) and never from inside a run(): the inner call would
   wait on the outer one, which is waiting on it. busy is true while anything
   is queued or running; held only while a task is running.

   This is the same window.AirlockCartLock the Guard pack swap, Autoship
   toggle and Guard combine take (cart-pack-variant-qty.js,
   cart-autoship-toggle.js, cart-guard-combine.js), so a gift write, a
   discount pass and a Guard change never overlap. All three files define it
   identically; whichever loads first wins. */
if (!window.AirlockCartLock) {
  window.AirlockCartLock = (() => {
    let tail = Promise.resolve();
    let pending = 0;
    const release = () => {
      pending -= 1;
    };
    const lock = {
      held: false,
      get busy() {
        return pending > 0;
      },
      run(fn) {
        pending += 1;
        const result = tail.then(async () => {
          lock.held = true;
          try {
            return await fn();
          } finally {
            lock.held = false;
          }
        });
        tail = result.then(release, release);
        return result;
      },
    };
    return lock;
  })();
}

// The last change attempted, kept in sessionStorage so it survives the cart
// page's reload: a change that failed and reloaded must not be retried on
// every load.
const readLastSync = () => {
  try {
    return JSON.parse(sessionStorage.getItem("giftSyncLast")) || { signature: "", at: 0 };
  } catch (e) {
    return { signature: "", at: 0 };
  }
};
const writeLastSync = (value) => {
  try {
    sessionStorage.setItem("giftSyncLast", JSON.stringify(value));
  } catch (e) {}
};

/* Raised at the end of a settle that read the cart's own verdict out of the
   section markup, and taken by the <cart-gift-sync> that the paint directly
   after it connects: that element's delayed /cart.js would only be asking a
   question the settle just answered. One shot, and only that one paint — the
   0-ms timer drops it before anything later (an add, say) can reach it. In
   memory on purpose: a reload has no settle behind it and must reconcile. */
let giftSettleFresh = false;
let giftSettleFreshTimer = null;

const markGiftSettleFresh = () => {
  giftSettleFresh = true;
  clearTimeout(giftSettleFreshTimer);
  giftSettleFreshTimer = setTimeout(() => {
    giftSettleFresh = false;
  }, 0);
};

const takeGiftSettleFresh = () => {
  const fresh = giftSettleFresh;
  giftSettleFresh = false;
  clearTimeout(giftSettleFreshTimer);
  return fresh;
};

const giftPost = (url, body) =>
  (window.untrackedCartFetch || window.fetch.bind(window))(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });

/* A 200 from a cart write is not the end of it. Shopify can still apply the
   next write against the cart from before this one, so a write sent right
   after another's response can undo it: a gift add that answered with its
   line, then a cart without the gift for good. Writes under the lock are
   spaced by this much before anything reads the result or writes again. */
const CART_SETTLE_MS = 700;
const cartSettle = (ms = CART_SETTLE_MS) => new Promise((resolve) => setTimeout(resolve, ms));

const readCart = async () => {
  try {
    const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } });
    return response.ok ? await response.json() : null;
  } catch (e) {
    return null;
  }
};

/* Every line carrying _upsell_discount_code whose _upsell_for parent is still
   in the cart needs its code applied. held is what the cart has now, missing
   what it should have and does not. */
const upsellCodeGap = (cart) => {
  const items = (cart && cart.items) || [];
  const held = ((cart && cart.discount_codes) || []).map((discount) => discount.code).filter(Boolean);
  const known = held.map((code) => String(code).toLowerCase());
  const missing = [];
  items.forEach((item) => {
    const code = item.properties && item.properties._upsell_discount_code;
    const parentId = item.properties && item.properties._upsell_for;
    if (!code || !parentId) return;
    if (!items.some((other) => other.id * 1 === parentId * 1)) return;
    if (known.includes(String(code).toLowerCase())) return;
    known.push(String(code).toLowerCase());
    missing.push(code);
  });
  return { held, missing };
};

// The missing codes, put back alongside the ones the cart already holds.
const applyUpsellCodes = async ({ held, missing }) => {
  if (!missing.length) return false;
  try {
    const response = await giftPost(`${routes.cart_update_url}.js`, { discount: [...held, ...missing].join(",") });
    return response.ok;
  } catch (e) {
    console.error(e);
    return false;
  }
};

/* The safety net behind the lock, run after a settle's gift writes and still
   inside it: a write from outside the lock (a Replo block's own discount
   call, say) can still land against a stale cart and take an upsell code with
   it. One attempt — the result is not read back and retried. True when codes
   were re-applied, so sections answered before it are out of date. */
const restoreUpsellCodes = async () => {
  const cart = await readCart();
  return cart ? applyUpsellCodes(upsellCodeGap(cart)) : false;
};

/* What of a gift change did not stick, judged against what it set out to do
   rather than re-planned from the cart: a line it set to a quantity that has
   another, and a tier it added a gift for that has none. */
const giftDrift = (cart, updates, adds) => {
  const items = (cart && cart.items) || [];
  const fix = { updates: {}, adds: [] };
  Object.keys(updates).forEach((key) => {
    const line = items.find((item) => item.key === key);
    if (line && line.quantity !== updates[key]) fix.updates[key] = updates[key];
  });
  fix.adds = adds.filter(
    (add) => !items.some((item) => item.properties && item.properties._gift === add.properties._gift)
  );
  fix.empty = !fix.adds.length && !Object.keys(fix.updates).length;
  return fix;
};

/* Run after a reconcile's gift writes, still under the lock: let them
   settle, read the cart back, and check both that the gifts are where the
   change put them and that every upsell code is held. Whatever is wrong is
   repaired once, gifts first and codes after, with a settle and a fresh read
   between the two so the code write starts from the gift write's cart. No
   second round: what is still wrong after this is left for the next
   reconcile. True when anything was re-written, so sections answered before
   it are out of date. */
const verifyGiftWrites = async (updates, adds) => {
  await cartSettle();
  let cart = await readCart();
  if (!cart) return false;
  let repaired = false;
  const drift = giftDrift(cart, updates, adds);
  if (!drift.empty) {
    try {
      if (Object.keys(drift.updates).length) {
        const response = await giftPost(`${routes.cart_update_url}.js`, { updates: drift.updates });
        if (response.ok) repaired = true;
        if (drift.adds.length) await cartSettle();
      }
      if (drift.adds.length) {
        const response = await giftPost(`${routes.cart_add_url}.js`, { items: drift.adds });
        if (response.ok) repaired = true;
      }
    } catch (e) {
      console.error(e);
    }
    await cartSettle();
    cart = await readCart();
    if (!cart) return repaired;
  }
  if (await applyUpsellCodes(upsellCodeGap(cart))) repaired = true;
  return repaired;
};

/* What to change, from a per-tier view of the cart: { tier, variant, eligible,
   target, lines: [key], quantity }. Both ways in build that shape — the
   snippet's [data-gift-state] already is it, the element derives it from the
   live cart — so the two can never drift into deciding differently. target is
   how many of the gift to hold: 1 by spend, more under Black Friday's per-unit
   rule (three OdorGuard for 3+ units). The gift is held on one line at that
   quantity. */
const planGifts = (tiers) => {
  const adds = [];
  const updates = {};
  tiers.forEach((tier) => {
    const target = tier.target || 1;
    if (tier.eligible && tier.lines.length === 0 && tier.variant) {
      adds.push({ id: tier.variant, quantity: target, properties: { _gift: tier.tier } });
    } else if (!tier.eligible) {
      tier.lines.forEach((key) => (updates[key] = 0));
    } else if (tier.quantity !== target || tier.lines.length > 1) {
      tier.lines.forEach((key, i) => (updates[key] = i === 0 ? target : 0));
    }
  });
  return { adds, updates, empty: !adds.length && !Object.keys(updates).length };
};

/* Black Friday's per-unit gift quantity for a tier, from the live cart: the
   AIRLOCK One / Voyage units it holds (gift lines aside), capped at 3, index
   the tier's targets. null when the tier is on the spend rule. */
const unitTarget = (tier, cart) => {
  if (!Array.isArray(tier.unitTargets) || !Array.isArray(tier.unitHandles)) return null;
  const units = cart.items
    .filter((item) => !(item.properties && item.properties._gift) && tier.unitHandles.includes(item.handle))
    .reduce((sum, item) => sum + item.quantity, 0);
  return tier.unitTargets[Math.min(units, 3)] || 0;
};

/* Settle the gift lines and hand back sections to paint, before the caller
   paints anything. drawerHtml is the cart-drawer section /cart/change.js (or
   the section rendering API) just answered with; the state the snippet wrote
   into it describes the cart as it now is, so no /cart.js read is needed and
   nothing is in flight to race.

   Returns null — paint what you had — when there is nothing to settle, when
   another cart write (a reconcile, a discount pass) holds the lock, or when
   the POST did not take.

   options.underLock: the caller is itself a task running under
   AirlockCartLock (a Guard pack swap, Autoship toggle or combine painting its
   own change). The lock is then already this caller's, so the settle runs in
   it directly: queuing would wait on the caller (a deadlock), and bailing on
   busy would paint a stale gift for the element's reconcile to fix a beat
   later (the flicker this exists to prevent). Only pass it from inside run();
   if the lock is not actually held it is ignored and the settle queues. */
window.settleGiftsBeforePaint = async function (drawerHtml, sectionIds, itemCount, options) {
  const underLock = !!(options && options.underLock) && window.AirlockCartLock.held;
  if ((!underLock && window.AirlockCartLock.busy) || typeof drawerHtml !== "string" || !drawerHtml) return null;
  /* The shared parse (cart.js): this is the same string the caller is about to
     swap into the drawer, and it only needs parsing once between the two. */
  const doc =
    typeof window.cartSectionDoc === "function"
      ? window.cartSectionDoc(drawerHtml)
      : new DOMParser().parseFromString(drawerHtml, "text/html");
  const data = doc.querySelector("[data-gift-state]");
  if (!data) return null;
  let tiers;
  try {
    tiers = JSON.parse(data.textContent);
  } catch (e) {
    return null;
  }

  const plan = planGifts(tiers);
  if (plan.empty) {
    // Nothing to settle, but the question was asked and answered: the element
    // this paint connects can skip asking it again. See markGiftSettleFresh.
    markGiftSettleFresh();
    return null;
  }

  /* The shared cart lock, held across both POSTs: a <cart-gift-sync> whose
     timer fires in here would read a cart mid-settle, see no gift line, and
     add a second one, and a discount POST in flight beside them would be
     undone by whichever landed last. Taken in the same turn as the busy check
     above, so nothing can queue in between. */
  const withSections = { sections: sectionIds, sections_url: window.location.pathname };
  let sections = null;
  let count = itemCount;
  const settle = async () => {
    try {
      let changed = false;
      if (Object.keys(plan.updates).length) {
        const response = await giftPost(`${routes.cart_update_url}.js`, { updates: plan.updates, ...withSections });
        if (!response.ok) return false;
        changed = true;
        const cart = await response.json();
        if (cart.sections) sections = cart.sections;
        if (typeof cart.item_count === "number") count = cart.item_count;
      }
      if (plan.adds.length) {
        const response = await giftPost(`${routes.cart_add_url}.js`, { items: plan.adds, ...withSections });
        /* An add that failed after a removal that took still leaves sections
           worth painting — they are the cart as it now stands. */
        if (response.ok) {
          changed = true;
          const added = await response.json();
          if (added.sections) sections = added.sections;
          /* add.js answers with the lines it added, not a cart, so there is no
             item_count in it. What was added is known exactly. */
          count += plan.adds.reduce((sum, item) => sum + item.quantity, 0);
        }
      }
      /* A code put back changes the totals the sections above were rendered
         with, so ask for them again; if that fails, the older ones still beat
         the markup from before the settle. */
      if (changed && (await restoreUpsellCodes())) {
        const ids = Array.isArray(sectionIds) ? sectionIds.join(",") : sectionIds;
        const response = await fetch(`${window.location.pathname}?sections=${ids}`);
        if (response.ok) sections = await response.json();
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };
  const took = await (underLock ? settle() : window.AirlockCartLock.run(settle));

  if (!took || !sections) return null;
  /* Leave the same record a reconcile would, so the fresh <cart-gift-sync>
     this paint connects does not repeat a change that is already applied. */
  writeLastSync({ signature: JSON.stringify([plan.adds, plan.updates]), at: Date.now() });
  markGiftSettleFresh();
  return { sections, itemCount: count };
};

// What a cart holds, line by line, for the reconcile's loop guard above.
const cartLinesSignature = (cart) =>
  ((cart && cart.items) || []).map((item) => `${item.key}x${item.quantity}`).join(",");

class CartGiftSync extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector("[data-gift-state]");
    if (!data) return;
    try {
      this.tiers = JSON.parse(data.textContent);
    } catch (e) {
      return;
    }
    /* Taken whether or not it is used, so it cannot carry over to a later
       paint. See markGiftSettleFresh. */
    const settled = takeGiftSettleFresh();
    /* The settle this paint came out of decided the gifts from the cart's own
       verdict, and this markup says there is nothing left to do — the read
       below would only confirm it a beat later.
       A cart carrying an upsell discount still reconciles: updateQuantity runs
       actualize() after this paint, which can move the totals (and so which
       tier is reached) out from under the verdict this markup was rendered
       with. Those carts keep today's delayed read. */
    if (settled && planGifts(this.tiers).empty && !this.hasUpsellDiscount()) return;
    this.timer = setTimeout(() => this.run(), 600);
  }

  // Any line in this drawer's list that an upsell discount code is riding on.
  hasUpsellDiscount() {
    const items = this.closest("cart-drawer-items") || this;
    const rows = items.querySelectorAll(".cart-item");
    for (const row of rows) {
      if (row.dataset.upsellDiscountCode) return true;
    }
    return false;
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  /* One reconcile at a time, held from before the cart read until the last
     POST is done. Claiming it inside sync() left the read unguarded: a drawer
     re-render during it swaps this element for a fresh one whose own reconcile
     had already passed the check, so both saw no gift line and both added the
     same gift — and two adds in flight at once land as two lines, not one.

     The lock is the shared cart lock, so a reconcile also waits out a discount
     pass in flight (cart.js) instead of writing over it, and the pass waits
     out the reconcile. A fresh element queues behind whatever holds it; one a
     re-render removed while it waited finds itself disconnected and lets the
     next one decide. */
  async run() {
    clearTimeout(this.timer);
    this.timer = null;
    if (!this.isConnected) return false;
    return !!(await window.AirlockCartLock.run(() => (this.isConnected ? this.reconcile() : false)));
  }

  async reconcile() {
    let cart;
    try {
      cart = await (await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } })).json();
    } catch (e) {
      return false;
    }
    if (!this.isConnected) return false;

    // Spend, as the snippet counts it: the total less any gift lines.
    const gifts = cart.items.filter((item) => item.properties && item.properties._gift);
    const spend = cart.total_price - gifts.reduce((sum, item) => sum + item.final_line_price, 0);

    /* The snippet's own eligible/lines/quantity describe the cart the markup
       was rendered from; re-derive them against the cart as it is now. */
    const plan = planGifts(
      this.tiers.map((tier) => {
        const lines = gifts.filter((item) => item.properties._gift === tier.tier);
        const byUnits = unitTarget(tier, cart);
        return {
          tier: tier.tier,
          variant: tier.variant,
          eligible: byUnits === null ? tier.ready && spend >= tier.threshold : tier.ready && byUnits > 0,
          target: byUnits === null ? 1 : byUnits,
          lines: lines.map((item) => item.key),
          quantity: lines.reduce((sum, item) => sum + item.quantity, 0),
        };
      })
    );
    if (plan.empty) return false;

    /* The same change against the same cart twice within a few seconds means
       it did not take. Stop rather than loop. The cart's lines are part of
       the signature: a shopper who goes 2 units -> 1 -> 2 -> 1 inside five
       seconds asks for the same OdorGuard removal twice, against two
       different carts, and the second must still go out. */
    const signature = JSON.stringify([plan.adds, plan.updates, cartLinesSignature(cart)]);
    const now = Date.now();
    const last = readLastSync();
    if (signature === last.signature && now - last.at < 5000) return false;
    writeLastSync({ signature, at: now });

    return this.sync(plan.updates, plan.adds);
  }

  async sync(updates, adds) {
    try {
      /* The drawer this change needs painting into can come back with the
         change itself, so ask for it here rather than fetching the same
         sections again afterwards. Not on the cart page: that reloads, and
         sections it would throw away are only a bigger response to download. */
      const onCartPage = !!document.querySelector("cart-items:not(cart-drawer-items)");
      const withSections = onCartPage
        ? {}
        : { sections: "cart-drawer,cart-icon-bubble", sections_url: window.location.pathname };
      // Only re-render (or reload, on the cart page) after a change that took.
      let changed = false;
      let sections = null;
      /* The last POST that took is the one describing the cart now, so its
         sections are the ones to paint — and if it came back without any,
         the ones before it are already out of date and refresh() fetches. */
      const read = async (response) => {
        if (!response.ok) return;
        changed = true;
        if (onCartPage) return;
        sections = null;
        try {
          const body = await response.json();
          if (body && body.sections) sections = body.sections;
        } catch (e) {}
      };
      if (Object.keys(updates).length) {
        await read(await giftPost(`${routes.cart_update_url}.js`, { updates, ...withSections }));
      }
      if (adds.length) {
        await read(await giftPost(`${routes.cart_add_url}.js`, { items: adds, ...withSections }));
      }
      if (changed) {
        // Still under the lock. See verifyGiftWrites.
        if (await verifyGiftWrites(updates, adds)) sections = null;
        await this.refresh(sections, updates);
      }
      return changed;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  // Re-render the drawer and the cart count. On the cart page the page's own
  // line list is built by a different section, so the page reloads instead.
  // sections is what the change POST answered with, when it carried them;
  // updates is what it changed.
  async refresh(sections, updates) {
    if (document.querySelector("cart-items:not(cart-drawer-items)")) {
      /* A held checkout (below) is about to leave for checkout with the cart
         as it now stands, so there is nothing to show here. */
      if (window.__cartGiftCheckoutHeld) return;
      /* Markup after the reload never shows a gift leaving, so a gift taken
         out fades on this page first. cart.js skips it under reduced motion. */
      const removed = Object.keys(updates || {}).filter((key) => updates[key] === 0);
      if (removed.length && typeof window.fadeOutCartRows === "function") {
        const rows = Array.from(
          document.querySelectorAll("cart-items:not(cart-drawer-items) .cart-item--gift[data-line-key]")
        ).filter((row) => removed.includes(row.dataset.lineKey));
        await window.fadeOutCartRows(rows);
      }
      window.location.reload();
      return;
    }
    if (!sections || !sections["cart-drawer"]) {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      sections = await response.json();
    }
    const swap = (html, selector, target) => {
      if (!html || !target) return;
      const doc =
        typeof window.cartSectionDoc === "function"
          ? window.cartSectionDoc(html)
          : new DOMParser().parseFromString(html, "text/html");
      const source = doc.querySelector(selector);
      if (!source) return;
      if (window.transformText && html.indexOf("AIRLOCK") !== -1) window.transformText(source);
      /* Through cart.js's writer, which keeps its own record of what each
         container holds — a write behind its back would make that record
         wrong for the next quantity change. */
      if (typeof setInnerHTMLIfChanged === "function") setInnerHTMLIfChanged(target, source.innerHTML);
      else if (target.innerHTML !== source.innerHTML) target.innerHTML = source.innerHTML;
    };
    // The drawer's inner content only, as a quantity change does: the overlay
    // and the drawer's own wiring stay put.
    const inner = document.querySelector("#CartDrawer .drawer__inner");
    // Whichever of the two scrolls on this viewport is rebuilt by the swap, so
    // a gift landing while the shopper is partway down the list would otherwise
    // jump them back to the top. See captureDrawerScroll in cart.js.
    const scroll = typeof captureDrawerScroll === "function" ? captureDrawerScroll() : null;
    swap(sections["cart-drawer"], ".drawer__inner", inner);
    if (typeof restoreDrawerScroll === "function") restoreDrawerScroll(scroll);
    swap(sections["cart-icon-bubble"], ".shopify-section", document.getElementById("cart-icon-bubble"));
    if (typeof window.refreshAffirmMessaging === "function") window.refreshAffirmMessaging();
  }
}

if (!customElements.get("cart-gift-sync")) {
  customElements.define("cart-gift-sync", CartGiftSync);
}

/* Bring the gift lines in step with the cart now, from outside the drawer.

   The cart page (/cart) re-renders its own line list after a quantity change
   or a remove, never the drawer, so the drawer's <cart-gift-sync> — the only
   thing that adds and removes gifts — was never asked again. Going from two
   Voyage to one on /cart left the 2-unit OdorGuard in the cart, and checkout
   took it (order #12784AL: one Voyage, a MoistureGuard and an OdorGuard).
   cart.js calls this after every change on /cart.

   Resolves true when a gift line was added or removed; on /cart that also
   reloads the page (refresh()). Never call it from inside an
   AirlockCartLock.run(): it queues on the lock. */
window.reconcileCartGifts = function () {
  const sync = document.querySelector("cart-gift-sync");
  if (!sync || !sync.tiers) return Promise.resolve(false);
  return sync.run().catch(() => false);
};

/* The cart page's own trigger, once the taps stop. A gift that changes
   reloads /cart (refresh()), and a reload in the middle of a run of +/- taps
   lost some of them (1 -> + - + - + ended on 3 instead of 2). So wait until
   no quantity request is in flight and nothing has changed for a moment. */
const CART_PAGE_GIFT_QUIET_MS = 900;
window.scheduleCartGiftReconcile = function () {
  clearTimeout(window.__cartPageGiftTimer);
  window.__cartPageGiftTimer = setTimeout(function fire() {
    if ((window.cartQuantityRequests || 0) > 0) {
      window.__cartPageGiftTimer = setTimeout(fire, CART_PAGE_GIFT_QUIET_MS);
      return;
    }
    window.__cartPageGiftTimer = null;
    window.reconcileCartGifts();
  }, CART_PAGE_GIFT_QUIET_MS);
};

/* The /cart checkout waits for the gift lines. A shopper who lands on /cart
   (or just changed it) and taps Checkout inside the reconcile's delay would
   otherwise check out with gifts the cart no longer earns. While a reconcile
   is due or any cart write holds the lock, the submit is held, the gifts are
   settled on the server's cart, and the same submit goes out again, so
   checkout opens on the corrected cart (the page does not reload in between).
   Capped at 4 seconds: a slow cart never blocks checkout. The drawer's form
   (#CartDrawer-Form) has its own submit guard in cart-drawer.js and settles
   gifts before it paints, so it is left alone. */
if (!window.__cartGiftCheckoutHold) {
  window.__cartGiftCheckoutHold = true;
  window.addEventListener(
    "submit",
    (evt) => {
      const form = evt.target;
      if (!form || form.id !== "cart" || form.dataset.giftsSettled === "1") return;
      const submitter = evt.submitter;
      if (submitter && submitter.name !== "checkout") return;
      const sync = document.querySelector("cart-gift-sync");
      /* A quantity change not yet sent (cart.js debounces +/- taps) or still
         in flight counts too: cart.js only schedules the reconcile once its
         request and repaint are done, so a tap on Checkout right after a
         minus would otherwise find nothing due and leave with the old gifts
         (the form's updates[] inputs would still apply the new quantity). */
      const changing = () => !!window.cartPageChangePending || (window.cartQuantityRequests || 0) > 0;
      const due = !!(sync && sync.tiers && (sync.timer || window.__cartPageGiftTimer || changing()));
      if (!due && !window.AirlockCartLock.busy) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const cap = new Promise((resolve) => setTimeout(() => resolve(false), 4000));
      window.__cartGiftCheckoutHeld = true;
      clearTimeout(window.__cartPageGiftTimer);
      window.__cartPageGiftTimer = null;
      // Let the quantity change land first, then settle the gifts on that cart.
      const settled = new Promise((resolve) => {
        const wait = () => (changing() ? setTimeout(wait, 100) : resolve());
        wait();
      }).then(() => {
        clearTimeout(window.__cartPageGiftTimer);
        window.__cartPageGiftTimer = null;
        return window.reconcileCartGifts();
      });
      Promise.race([settled, cap]).then(() => {
        form.dataset.giftsSettled = "1";
        if (typeof form.requestSubmit === "function") form.requestSubmit(submitter || undefined);
        else form.submit();
        form.dataset.giftsSettled = "";
      });
    },
    true
  );
  // Back from checkout (back/forward cache): the page is live again.
  window.addEventListener("pageshow", () => {
    window.__cartGiftCheckoutHeld = false;
  });
}
